package com.fantasytenis.web.components.skills

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import com.fantasytenis.shared.domain.lineup.entity.Category
import com.fantasytenis.shared.domain.skills.entity.PlayerSkillCosts
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin

private const val SVG_NS = "http://www.w3.org/2000/svg"

/**
 * Radar chart component for visualizing player skills
 *
 * @param costs Player skill costs to display
 * @param size Chart size in pixels
 * @param showLabels Whether to show category labels
 */
@Composable
fun RadarChart(
    costs: PlayerSkillCosts,
    size: Int = 200,
    showLabels: Boolean = true
) {
    val center = size / 2.0
    val maxRadius = center * 0.7 // Leave room for labels
    val categories = listOf(
        Pair(Category.SERVE, costs.serve),
        Pair(Category.FOREHAND, costs.forehand),
        Pair(Category.BACKHAND, costs.backhand),
        Pair(Category.VOLLEYS, costs.volleys),
        Pair(Category.FIGHTING, costs.fighting)
    )

    // Calculate points for the polygon
    val points = categories.mapIndexed { index, (_, value) ->
        val angle = (2 * PI * index / 5) - (PI / 2) // Start from top
        val radius = (value / 12.0) * maxRadius
        Pair(
            center + radius * cos(angle),
            center + radius * sin(angle)
        )
    }

    // Calculate label positions (further out than the chart)
    val labelRadius = center * 0.95
    val labelPositions = categories.mapIndexed { index, (category, _) ->
        val angle = (2 * PI * index / 5) - (PI / 2)
        Triple(
            center + labelRadius * cos(angle),
            center + labelRadius * sin(angle),
            category
        )
    }

    Div({
        style {
            position(Position.Relative)
            width(size.px)
            height(size.px)
        }
    }) {
        // SVG container
        Div({
            attr("data-radar-chart", "true")
            style {
                width(size.px)
                height(size.px)
            }
        }) {
            DisposableEffect(costs, size) {
                val container = scopeElement

                // Clear existing SVG
                while (container.firstChild != null) {
                    container.removeChild(container.firstChild!!)
                }

                // Create SVG element
                val svg = document.createElementNS(SVG_NS, "svg").apply {
                    setAttribute("width", "$size")
                    setAttribute("height", "$size")
                    setAttribute("viewBox", "0 0 $size $size")
                }

                // Grid circles at 4, 8, 12
                listOf(4, 8, 12).forEach { level ->
                    val radius = (level / 12.0) * maxRadius
                    val circle = document.createElementNS(SVG_NS, "circle").apply {
                        setAttribute("cx", "$center")
                        setAttribute("cy", "$center")
                        setAttribute("r", "$radius")
                        setAttribute("fill", "none")
                        setAttribute("stroke", "#E0E0E0")
                        setAttribute("stroke-width", "1")
                        if (level != 12) {
                            setAttribute("stroke-dasharray", "2,2")
                        }
                    }
                    svg.appendChild(circle)
                }

                // Grid lines from center to each vertex
                repeat(5) { index ->
                    val angle = (2 * PI * index / 5) - (PI / 2)
                    val endX = center + maxRadius * cos(angle)
                    val endY = center + maxRadius * sin(angle)
                    val line = document.createElementNS(SVG_NS, "line").apply {
                        setAttribute("x1", "$center")
                        setAttribute("y1", "$center")
                        setAttribute("x2", "$endX")
                        setAttribute("y2", "$endY")
                        setAttribute("stroke", "#E0E0E0")
                        setAttribute("stroke-width", "1")
                    }
                    svg.appendChild(line)
                }

                // Data polygon
                val pathData = points.mapIndexed { index, (x, y) ->
                    if (index == 0) "M $x $y" else "L $x $y"
                }.joinToString(" ") + " Z"

                val path = document.createElementNS(SVG_NS, "path").apply {
                    setAttribute("d", pathData)
                    setAttribute("fill", "rgba(0, 166, 81, 0.3)")
                    setAttribute("stroke", "#00A651")
                    setAttribute("stroke-width", "2")
                }
                svg.appendChild(path)

                // Data points
                points.forEachIndexed { index, (x, y) ->
                    val category = categories[index].first
                    val circle = document.createElementNS(SVG_NS, "circle").apply {
                        setAttribute("cx", "$x")
                        setAttribute("cy", "$y")
                        setAttribute("r", "5")
                        setAttribute("fill", getCategoryColorHex(category))
                        setAttribute("stroke", "#FFFFFF")
                        setAttribute("stroke-width", "2")
                    }
                    svg.appendChild(circle)
                }

                container.appendChild(svg)

                onDispose {
                    while (container.firstChild != null) {
                        container.removeChild(container.firstChild!!)
                    }
                }
            }
        }

        // Labels (positioned absolutely)
        if (showLabels) {
            labelPositions.forEach { (x, y, category) ->
                val value = costs.getCostForCategory(category)
                Div({
                    style {
                        position(Position.Absolute)
                        left(x.px)
                        top(y.px)
                        property("transform", "translate(-50%, -50%)")
                        fontSize(11.px)
                        fontWeight(600)
                        color(getCategoryColor(category))
                        textAlign("center")
                    }
                }) {
                    Div { org.jetbrains.compose.web.dom.Text(category.displayName) }
                    Div({
                        style {
                            fontSize(12.px)
                            fontWeight(700)
                        }
                    }) {
                        org.jetbrains.compose.web.dom.Text("$value")
                    }
                }
            }
        }
    }
}

/**
 * Get hex color for category (for SVG attributes)
 */
private fun getCategoryColorHex(category: Category): String {
    return when (category) {
        Category.SERVE -> "#2563eb"
        Category.FOREHAND -> "#16a34a"
        Category.BACKHAND -> "#dc2626"
        Category.VOLLEYS -> "#9333ea"
        Category.FIGHTING -> "#ea580c"
    }
}

/**
 * Mini radar chart for compact displays
 */
@Composable
fun MiniRadarChart(
    costs: PlayerSkillCosts,
    size: Int = 80
) {
    RadarChart(costs = costs, size = size, showLabels = false)
}
